import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { QueueResult } from '@/domain/queue';
import { useAppState, type AppStateValue } from '@/ui/AppState';
import { navigate, useRoute } from '@/ui/router';
import { CategoryChip, EmptyState, FlagBanner, SessionChip } from '@/ui/components/bits';
import { SessionPicker } from '@/ui/components/SessionPicker';
import { isSingleSessionMode, shuffle } from '@/domain/select';
import { buildQueue } from '@/domain/queue';
import { recordAnswer } from '@/domain/practice';
import { clearRun, loadRun, runKey, saveRun } from '@/domain/resume';
import type { Item, SessionKind, StudyMode } from '@/domain/types';

export function StudyScreen() {
  const route = useRoute();
  const mode = (route.params.get('mode') ?? 'cumulative') as StudyMode;
  const sessionId = route.params.get('session') ?? undefined;
  const category = route.params.get('category') ?? undefined;
  /**
   * Several categories at once, comma-separated — one part of the mid-term
   * paper spans more than one topic. The raw string identifies the run.
   */
  const categories = route.params.get('categories') ?? undefined;
  const title = route.params.get('title') ?? undefined;
  /**
   * Force the textbook chapters into the draw. Cumulative leaves them out by
   * default so his own classes are not buried, but a drill aimed at one part
   * of the paper wants the whole book: that is where most of the questions
   * for chapters 1-6 are.
   */
  const textbook = route.params.get('textbook') === '1';
  const nonce = route.params.get('t') ?? '';

  if (isSingleSessionMode(mode) && !sessionId) {
    return (
      <div className="stack">
        <h1>{pickerHeading(mode)}</h1>
        <p className="muted">Pick a session to study its flashcards.</p>
        <SessionPicker
          kind={pickerKind(mode)}
          want="flashcard"
          onPick={(id) => navigate('/study', { mode, session: id })}
        />
      </div>
    );
  }

  return (
    <FlashcardLoader
      key={`${mode}|${sessionId ?? ''}|${category ?? ''}|${categories ?? ''}|${nonce}`}
      mode={mode}
      sessionId={sessionId}
      category={category}
      categories={categories}
      heading={title}
      textbook={textbook}
      nonce={nonce}
    />
  );
}

interface Start {
  built: QueueResult;
  index: number;
  graded: Record<string, boolean>;
  /** True when this deck was picked up rather than drawn. */
  resumed: boolean;
}

interface RunnerProps {
  mode: StudyMode;
  sessionId?: string;
  category?: string;
  /** Comma-separated, as it came from the URL. */
  categories?: string;
  /** A name the caller has for this deck, e.g. a part of the paper. */
  heading?: string;
  /** Draw from the textbook chapters too, whatever the usual setting says. */
  textbook?: boolean;
  nonce: string;
}

/**
 * Draws the deck, or picks up the one left half-finished — including the order
 * it was in, so a shuffle survives being closed. Reading the saved place is a
 * database read, so it happens here and the runner starts from a settled deck.
 */
function drawFrom(
  now: AppStateValue,
  {
    mode,
    sessionId,
    category,
    categories,
    textbook,
    excludeIds,
  }: {
    mode: StudyMode;
    sessionId?: string;
    category?: string;
    categories?: readonly string[];
    textbook?: boolean;
    excludeIds?: Set<string>;
  },
): QueueResult {
  return buildQueue({
    items: now.items,
    sessions: now.sessions,
    mode,
    sessionId,
    category,
    categories,
    want: 'flashcard',
    progress: now.progress,
    levels: now.levels,
    override: now.local?.difficultyOverride ?? 'auto',
    now: Date.now(),
    limit: isSingleSessionMode(mode) ? undefined : (now.local?.cumulativeCardCount ?? 40),
    focusLatestClass: mode === 'cumulative' && Boolean(now.local?.focusLatestClass),
    includeTextbook: textbook || Boolean(now.local?.includeTextbookInCumulative),
    ...(excludeIds ? { excludeIds } : {}),
  });
}

function FlashcardLoader(props: RunnerProps) {
  const { mode, sessionId, category, categories, textbook, nonce } = props;
  const categoryList = categories ? categories.split(',').filter(Boolean) : undefined;
  const state = useAppState();
  const latest = useRef(state);
  latest.current = state;

  const [start, setStart] = useState<Start | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const now = latest.current;
      const resumed = await loadRun(
        runKey({ want: 'flashcard', mode, sessionId, category: categories ?? category }),
        nonce,
        now.items,
      );
      if (cancelled) return;
      if (resumed) {
        setStart({
          built: { items: resumed.items, deduped: 0, usedFallback: false },
          index: resumed.index,
          graded: resumed.graded,
          resumed: true,
        });
        return;
      }
      setStart({
        built: drawFrom(now, { mode, sessionId, category, categories: categoryList, textbook }),
        index: 0,
        graded: {},
        resumed: false,
      });
    })();
    return () => {
      cancelled = true;
    };
    // Drawn once, on mount. A new key remounts this whole component.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!start) return <p className="muted">Loading…</p>;
  return <FlashcardRunner {...props} start={start} />;
}

function FlashcardRunner({
  mode,
  sessionId,
  category,
  categories,
  heading,
  textbook,
  nonce,
  start,
}: RunnerProps & { start: Start }) {
  const categoryList = categories ? categories.split(',').filter(Boolean) : undefined;
  const state = useAppState();
  const { sessions, progress, toast, reload } = state;
  const [index, setIndex] = useState(start.index);
  const [flipped, setFlipped] = useState(false);
  const [graded, setGraded] = useState<Record<string, boolean>>(start.graded);

  const answeredRef = useRef<Set<string>>(new Set(Object.keys(start.graded)));

  // The queue is drawn once and then left alone. Recording an answer reloads
  // the data, and rebuilding from it would shuffle the deck under the reader
  // and throw away their position. Only New mix draws again.
  const latest = useRef(state);
  latest.current = state;

  const draw = useCallback(
    (): QueueResult =>
      drawFrom(latest.current, {
        mode,
        sessionId,
        category,
        categories: categoryList,
        textbook,
        excludeIds: answeredRef.current,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mode, sessionId, category, categories, textbook],
  );

  const [built, setBuilt] = useState<QueueResult>(start.built);
  const order = built.items;
  const setOrder = (next: Item[]) => setBuilt((b) => ({ ...b, items: next }));
  // Must match the key the loader read from, so a deck of several
  // categories saves its place under the same name it was found by.
  const key = runKey({ want: 'flashcard', mode, sessionId, category: categories ?? category });

  /**
   * The place is saved after every move and every grade, so closing the app
   * mid-deck and coming back lands on the same card, in the same order.
   */
  useEffect(() => {
    if (order.length === 0) return;
    void saveRun({
      want: 'flashcard',
      mode,
      sessionId,
      category: categories ?? category,
      nonce,
      items: order,
      index,
      graded,
    });
  }, [key, order, index, graded, mode, sessionId, category, nonce]);

  const current = order[index];
  const sessionLabel = useMemo(
    () => sessions.find((s) => s.id === current?.sessionId)?.shortLabel ?? '',
    [sessions, current?.sessionId],
  );

  const go = useCallback(
    (delta: number) => {
      setFlipped(false);
      setIndex((i) => Math.min(Math.max(i + delta, 0), Math.max(order.length - 1, 0)));
    },
    [order.length],
  );

  const grade = useCallback(
    async (correct: boolean) => {
      if (!current) return;
      setGraded((g) => ({ ...g, [current.id]: correct }));
      answeredRef.current.add(current.id);
      await recordAnswer({ item: current, correct, mode });
      await reload();
      if (index < order.length - 1) go(1);
      else setFlipped(false);
    },
    [current, index, order.length, go, mode, reload],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA)$/.test(target.tagName)) return;
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        setFlipped((f) => !f);
      } else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go]);

  if (order.length === 0) {
    return (
      <EmptyState icon="▤" title="Nothing to study here">
        {(heading ?? category)
          ? `No flashcards in ${heading ?? category} at the moment.`
          : 'This selection has no flashcards yet.'}
      </EmptyState>
    );
  }

  if (!current) return null;

  const title = heading ?? category ?? modeTitle(mode, sessions, sessionId);

  return (
    <div className="stack">
      <div className="spread">
        <h1>{title}</h1>
        <button
          className="btn btn--sm"
          onClick={() => navigate('/quiz', { mode, session: sessionId, category, categories })}
        >
          Quiz instead
        </button>
      </div>

      {start.resumed && index === start.index ? (
        <div className="banner banner--ok">
          <strong>Picked up where you left off.</strong>
          <div>
            Card {start.index + 1} of {order.length}, in the order you had them.{' '}
            <button
              className="btn btn--sm"
              onClick={() => {
                void clearRun(key);
                navigate('/study', {
                  mode,
                  session: sessionId,
                  category,
                  categories,
                  t: String(Date.now()),
                });
              }}
            >
              Start a fresh deck
            </button>
          </div>
        </div>
      ) : null}

      {built.usedFallback ? (
        <p className="faint" style={{ margin: 0 }}>
          Nothing is due right now — showing everything so you can review anyway.
        </p>
      ) : null}
      {built.deduped > 0 ? (
        <p className="faint" style={{ margin: 0 }}>
          {built.deduped} duplicate{built.deduped === 1 ? '' : 's'} of chapter-review cards left out.
        </p>
      ) : null}

      <div className="flashcard">
        <button
          className={`flashcard__inner${flipped ? ' is-flipped' : ''}`}
          onClick={() => setFlipped((f) => !f)}
          aria-label={flipped ? 'Show the front' : 'Show the back'}
        >
          <div className="flashcard__face" aria-hidden={flipped}>
            <span className="flashcard__label">Front</span>
            <div className="flashcard__text">{current.front}</div>
            <div className="flashcard__meta">
              <CategoryChip category={current.category} />
              {sessionLabel ? <SessionChip label={sessionLabel} /> : null}
              <BoxChip box={progress.get(current.id)?.box} />
            </div>
          </div>
          <div className="flashcard__face flashcard__face--back" aria-hidden={!flipped}>
            <span className="flashcard__label">Back</span>
            <div className="flashcard__text">{current.back}</div>
            {current.flag ? <FlagBanner flag={current.flag} text={current.flagText} /> : null}
          </div>
        </button>
      </div>

      <div className="counter">
        {index + 1} / {order.length}
        {graded[current.id] !== undefined ? (
          <span style={{ color: graded[current.id] ? 'var(--correct)' : 'var(--incorrect)' }}>
            {' '}
            · {graded[current.id] ? 'got it' : 'missed'}
          </span>
        ) : null}
      </div>

      {flipped ? (
        <div className="row" style={{ justifyContent: 'center' }}>
          <button className="btn btn--good" onClick={() => void grade(true)}>
            Got it
          </button>
          <button className="btn btn--bad" onClick={() => void grade(false)}>
            Missed it
          </button>
        </div>
      ) : (
        <p className="faint" style={{ textAlign: 'center', margin: 0 }}>
          Tap the card, or press Space, to flip it.
        </p>
      )}

      <div className="row" style={{ justifyContent: 'space-between' }}>
        <button className="btn" onClick={() => go(-1)} disabled={index === 0}>
          ‹ Previous
        </button>
        <button
          className="btn"
          onClick={() => {
            setOrder(shuffle(order));
            setIndex(0);
            setFlipped(false);
          }}
        >
          Shuffle
        </button>
        <button className="btn" onClick={() => go(1)} disabled={index >= order.length - 1}>
          Next ›
        </button>
      </div>

      {mode === 'cumulative' ? (
        <button
          className="btn btn--block"
          onClick={() => {
            void clearRun(key);
            answeredRef.current = new Set();
            setGraded({});
            const fresh = draw();
            setBuilt(fresh);
            setIndex(0);
            setFlipped(false);
            toast(`New mix — ${fresh.items.length} cards.`);
          }}
        >
          New mix
        </button>
      ) : null}
    </div>
  );
}

function BoxChip({ box }: { box?: number }) {
  if (!box) return <span className="chip chip--count">new</span>;
  return <span className="chip chip--count">box {box}</span>;
}

export function modeTitle(
  mode: StudyMode,
  sessions: { id: string; shortLabel: string }[],
  sessionId?: string,
): string {
  if (mode === 'cumulative') return 'Cumulative review';
  if (mode === 'practice') return 'Practice';
  const label = sessions.find((s) => s.id === sessionId)?.shortLabel;
  if (label) return label;
  if (mode === 'chapter') return 'Chapter review';
  if (mode === 'textbook') return 'Textbook';
  return 'Class';
}

/** Which kind of session the picker lists for a single-session mode. */
export function pickerKind(mode: StudyMode): SessionKind {
  if (mode === 'chapter') return 'chapter';
  if (mode === 'textbook') return 'textbook';
  return 'class';
}

export function pickerHeading(mode: StudyMode): string {
  if (mode === 'chapter') return 'Chapter review';
  if (mode === 'textbook') return 'Textbook chapter';
  return 'Choose class';
}
